'use client';
import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { useSiteLogo, FALLBACK_LOGO } from '@/lib/useSiteLogo';

const footerSolutions = [
  { label: 'Budgeting & Forecasting', href: '#solutions' },
  { label: 'Management Accounts', href: '#solutions' },
  { label: 'Performance Appraisal', href: '#solutions' },
  { label: 'Project Appraisal', href: '#solutions' },
  { label: 'Fuel Pricing', href: '#solutions' },
  { label: 'Advanced Excel Training', href: '#solutions' },
];

const footerAbout = [
  { label: 'Why Celsoft', href: '#about' },
  { label: 'Newsroom', href: '#news' },
  { label: 'Contact Us', href: '#contact' },
];

const footerSupport = [
  { label: 'Support Login', href: '/client-portal' },
  { label: 'Knowledge Base', href: '#' },
  { label: 'Give Feedback', href: '#' },
];

export default function PublicFooter() {
  const { logoUrl, isCustomLogo } = useSiteLogo();

  return (
    <footer style={{ background: '#071318', color: 'white' }}>
      {/* Top accent line — reference site style */}
      <div
        className="h-[1px]"
        style={{ background: 'linear-gradient(90deg, transparent, rgba(30,82,94,0.4), rgba(141,197,62,0.3), transparent)' }}
      />

      {/* Main footer */}
      <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-10">
        <div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 pb-12"
          style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}
        >
          {/* Brand */}
          <div>
            <div className="mb-5">
              <img
                src={logoUrl || FALLBACK_LOGO}
                alt="Celsoft Corporation footer logo"
                className="h-auto w-auto object-contain"
                style={{
                  maxHeight: isCustomLogo ? '600px' : '240px',
                  maxWidth: isCustomLogo ? '2160px' : '240px',
                  filter: isCustomLogo ? 'none' : 'brightness(0) invert(1)',
                }}
              />
            </div>
            <p className="text-sm leading-relaxed mb-6" style={{ color: 'rgba(255,255,255,0.45)' }}>
              Reliable, efficient &amp; effective financial modelling solutions — tailored to your needs.
            </p>
            <div
              className="w-12 h-[2px] mb-6"
              style={{ background: 'linear-gradient(90deg, #8dc53e, transparent)' }}
            />
            <p className="text-xs mb-1 uppercase tracking-widest" style={{ color: 'rgba(255,255,255,0.25)' }}>Free Support 24/7</p>
            <p className="text-lg font-bold text-white">(+263) 772 746 329</p>
            <div className="flex items-center gap-2 mt-5">
              {['F', 'T', 'I', 'Y', 'L']?.map((s) => (
                <a
                  key={s}
                  href="#"
                  aria-label={s}
                  className="w-8 h-8 rounded-xl flex items-center justify-center transition-all duration-200 hover:scale-110"
                  style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)' }}
                >
                  <span className="text-xs font-bold" style={{ color: 'rgba(255,255,255,0.45)' }}>{s}</span>
                </a>
              ))}
            </div>
          </div>

          {/* Solutions */}
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-widest mb-6" style={{ color: 'rgba(255,255,255,0.35)' }}>Modelling Solutions</h4>
            <ul className="flex flex-col gap-3">
              {footerSolutions?.map((item) => (
                <li key={item?.label}>
                  <Link
                    href={item?.href}
                    className="text-sm flex items-center gap-2 group transition-colors"
                    style={{ color: 'rgba(255,255,255,0.55)' }}
                  >
                    <span
                      className="w-1 h-1 rounded-full shrink-0 transition-colors"
                      style={{ background: 'rgba(141,197,62,0.4)' }}
                    />
                    {item?.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* About + Support */}
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-widest mb-6" style={{ color: 'rgba(255,255,255,0.35)' }}>Company</h4>
            <ul className="flex flex-col gap-3 mb-8">
              {footerAbout?.map((item) => (
                <li key={item?.label}>
                  <Link
                    href={item?.href}
                    className="text-sm flex items-center gap-2 transition-colors"
                    style={{ color: 'rgba(255,255,255,0.55)' }}
                  >
                    <span className="w-1 h-1 rounded-full shrink-0" style={{ background: 'rgba(141,197,62,0.4)' }} />
                    {item?.label}
                  </Link>
                </li>
              ))}
            </ul>
            <h4 className="text-sm font-semibold uppercase tracking-widest mb-6" style={{ color: 'rgba(255,255,255,0.35)' }}>Support</h4>
            <ul className="flex flex-col gap-3">
              {footerSupport?.map((item) => (
                <li key={item?.label}>
                  <Link
                    href={item?.href}
                    className="text-sm flex items-center gap-2 transition-colors"
                    style={{ color: 'rgba(255,255,255,0.55)' }}
                  >
                    <span className="w-1 h-1 rounded-full shrink-0" style={{ background: 'rgba(141,197,62,0.4)' }} />
                    {item?.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact + CTA */}
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-widest mb-6" style={{ color: 'rgba(255,255,255,0.35)' }}>Get In Touch</h4>
            <div className="flex flex-col gap-4 mb-8">
              <div>
                <p className="text-xs uppercase tracking-widest mb-1" style={{ color: 'rgba(255,255,255,0.25)' }}>Email</p>
                <a
                  href="mailto:celsoft@celsoftcorporation.com"
                  className="text-sm transition-colors"
                  style={{ color: 'rgba(255,255,255,0.55)' }}
                >
                  celsoft@celsoftcorporation.com
                </a>
              </div>
              <div>
                <p className="text-xs uppercase tracking-widest mb-1" style={{ color: 'rgba(255,255,255,0.25)' }}>Phone</p>
                <a
                  href="tel:+2637727463291"
                  className="text-sm transition-colors"
                  style={{ color: 'rgba(255,255,255,0.55)' }}
                >
                  (+263) 772 746 329
                </a>
              </div>
            </div>

            {/* Mini CTA — reference site bento style */}
            <div
              className="rounded-[20px] p-5"
              style={{ background: 'rgba(141,197,62,0.08)', border: '1px solid rgba(141,197,62,0.2)' }}
            >
              <p className="text-white font-bold text-sm mb-1">Ready to get started?</p>
              <p className="text-xs mb-4" style={{ color: 'rgba(255,255,255,0.45)' }}>45-Day Money-Back Guarantee</p>
              <Link
                href="#contact"
                className="block text-center px-4 py-2.5 rounded-full font-bold text-[#0d2229] text-xs uppercase tracking-wide transition-all hover:brightness-110"
                style={{ background: 'linear-gradient(135deg, #8dc53e, #a8d85a)' }}
              >
                Book Free Demo
              </Link>
            </div>
          </div>
        </div>

        {/* Bottom — reference site style */}
        <div className="pt-6 flex flex-col md:flex-row justify-between items-center gap-3">
          <p className="text-xs text-center md:text-left" style={{ color: 'rgba(255,255,255,0.25)' }}>
            © 2006 – 2026 · Celsoft Corporation · Powered by{' '}
            <a href="https://quantrix.com" target="_blank" rel="noopener noreferrer" className="text-[#8dc53e] hover:underline">Quantrix</a>
            {' '}· All Rights Reserved
          </p>
          <div className="flex items-center gap-4">
            <Link href="#" className="text-xs transition-colors" style={{ color: 'rgba(255,255,255,0.25)' }}>Privacy Policy</Link>
            <Link href="#" className="text-xs transition-colors" style={{ color: 'rgba(255,255,255,0.25)' }}>Terms of Service</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}